import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { buildWhatsAppLink } from "@/lib/site";

const faqs = [
  {
    id: "c-section",
    q: "Is your care safe after a C-section?",
    a: "Yes. Our doctors design a modified protocol for mothers recovering from a C-section, gentler abhyanga timing, incision-aware positioning, and lighter Kizhi sequences. We always begin with a clinical review and only proceed when your wound has healed sufficiently.",
  },
  {
    id: "logistics",
    q: "How does arrival and travel work?",
    a: "We're located at Paruthipra, Shoranur, in Palakkad, Kerala, well connected by road and rail. We help coordinate airport / station pickup from Coimbatore, Kochi or Calicut, and brief your family on what to pack. A welcome briefing happens on day one.",
  },
  {
    id: "bystander",
    q: "Can my husband or mother stay with me?",
    a: "Yes. Each villa includes accommodation for one bystander, with their own bed and three meals a day. Most mothers find this comforting, especially in the first weeks.",
  },
  {
    id: "baby-care",
    q: "Do you care for the baby too?",
    a: "Our caregivers are senior, experienced women trained to assist with bathing, swaddling and feeding routines. The pediatric primary remains your own doctor, we coordinate with them when needed.",
  },
  {
    id: "when-to-book",
    q: "When should I book?",
    a: "We host only 3 families at a time, so most months are reserved well in advance. We recommend reaching out as soon as your due date is confirmed, typically 3 to 5 months ahead.",
  },
];

export const FAQ = () => {
  return (
    <section
      id="faq"
      className="py-16 md:py-36 bg-ivory"
      data-testid="faq-section"
    >
      <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl"
        >
          <div className="text-xs uppercase tracking-[0.22em] text-sage font-medium">
            Questions, answered
          </div>
          <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl text-charcoal leading-tight tracking-tight">
            Things mothers usually ask.
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mt-12"
        >
          <Accordion
            type="multiple"
            defaultValue={faqs.map((f) => f.id)}
            className="w-full"
          >
            {faqs.map((f) => (
              <AccordionItem
                key={f.id}
                value={f.id}
                className="border-b border-sage/20"
                data-testid={`faq-item-${f.id}`}
              >
                <AccordionTrigger className="py-6 text-left font-serif text-lg md:text-xl text-charcoal hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="pb-6 text-base text-charcoal/70 leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="mt-8 text-center sm:text-left">
            <Link
              to="/faq"
              className="inline-flex items-center gap-2 text-sm font-medium text-sage hover:text-sage-700 transition-colors"
              data-testid="faq-more-questions-link"
            >
              More questions
              <ArrowRight size={14} />
            </Link>
          </div>

          <div className="mt-12 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-6 md:p-8 rounded-2xl bg-sand/60 border border-sage/15">
            <div>
              <div className="font-serif text-xl text-charcoal">Still wondering?</div>
              <p className="text-sm text-charcoal/65 mt-1">
                Speak with us directly on WhatsApp, we usually reply within an hour.
              </p>
            </div>
            <a
              href={buildWhatsAppLink("default")}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center bg-sage text-ivory px-6 py-3 rounded-full text-sm font-medium hover:bg-sage-700 transition-colors"
              data-testid="faq-whatsapp-cta"
            >
              Message on WhatsApp
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;
